import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useToast } from '@/hooks/useToast';
import { BarcodeScanner, BarcodeResult } from '@/components/inventory/BarcodeScanner';
import {
  Package,
  Search,
  Plus,
  Minus,
  Camera,
  MapPin,
  AlertTriangle,
  Clock,
  Filter,
  Grid3X3,
  List,
  ScanBarcode,
  RefreshCw,
  ChevronRight,
  Check,
  X,
} from 'lucide-react';

interface InventoryItem {
  id: string;
  item_id: string;
  item_code: string;
  item_name: string;
  category: string;
  location: string;
  location_code: string;
  quantity: number;
  min_stock: number;
  batch_number?: string;
  expiry_date?: string;
  status: 'in_stock' | 'low_stock' | 'out_of_stock' | 'expired';
}

interface MobileInventoryPageProps {
  items?: InventoryItem[];
}

// Mock data for demonstration
const mockItems: InventoryItem[] = [
  {
    id: '1',
    item_id: 'item-001',
    item_code: 'MED-001',
    item_name: 'Surgical Gloves',
    category: 'Surgical Supplies',
    location: 'Main Warehouse',
    location_code: 'WH-001',
    quantity: 500,
    min_stock: 100,
    batch_number: 'BATCH-2026-001',
    status: 'in_stock',
  },
  {
    id: '2',
    item_id: 'item-002',
    item_code: 'MED-002',
    item_name: 'Syringes 5ml',
    category: 'Medical Supplies',
    location: 'Pharmacy',
    location_code: 'PH-001',
    quantity: 45,
    min_stock: 50,
    batch_number: 'BATCH-2026-002',
    status: 'low_stock',
  },
  {
    id: '3',
    item_id: 'item-003',
    item_code: 'MED-003',
    item_name: 'Bandages',
    category: 'Consumables',
    location: 'Main Warehouse',
    location_code: 'WH-001',
    quantity: 0,
    min_stock: 20,
    status: 'out_of_stock',
  },
  {
    id: '4',
    item_id: 'item-004',
    item_code: 'MED-004',
    item_name: 'IV Set',
    category: 'Medical Equipment',
    location: 'ICU Storage',
    location_code: 'ICU-001',
    quantity: 30,
    min_stock: 25,
    batch_number: 'BATCH-2025-045',
    expiry_date: '2026-03-15',
    status: 'low_stock',
  },
  {
    id: '5',
    item_id: 'item-005',
    item_code: 'MED-005',
    item_name: 'Paracetamol 500mg',
    category: 'Pharmaceuticals',
    location: 'Pharmacy',
    location_code: 'PH-001',
    quantity: 200,
    min_stock: 50,
    batch_number: 'BATCH-2025-100',
    expiry_date: '2027-01-01',
    status: 'in_stock',
  },
];

export default function MobileInventoryPage({ items = mockItems }: MobileInventoryPageProps) {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [showScanner, setShowScanner] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [adjustQuantity, setAdjustQuantity] = useState(0);
  const [scanHistory, setScanHistory] = useState<BarcodeResult[]>([]);

  // Filter items
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      !searchQuery ||
      item.item_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.item_code.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation =
      selectedLocation === 'all' || item.location_code === selectedLocation;
    return matchesSearch && matchesLocation;
  });

  const locations = [...new Set(items.map((i) => i.location_code))];

  const getStatusBadge = (status: InventoryItem['status']) => {
    switch (status) {
      case 'in_stock':
        return <Badge variant="success">In Stock</Badge>;
      case 'low_stock':
        return <Badge variant="warning">Low Stock</Badge>;
      case 'out_of_stock':
        return <Badge variant="destructive">Out of Stock</Badge>;
      case 'expired':
        return <Badge variant="destructive">Expired</Badge>;
    }
  };

  const handleScan = useCallback(
    (result: BarcodeResult) => {
      setScanHistory((prev) => [result, ...prev.slice(0, 9)]);
      
      // Find matching item
      const matchedItem = items.find(
        (item) =>
          item.item_code === result.parsed.itemCode ||
          item.batch_number === result.parsed.batchNumber
      );

      if (matchedItem) {
        setSelectedItem(matchedItem);
        toast({
          title: 'Item Found',
          description: matchedItem.item_name,
        });
      } else {
        toast({
          title: 'Item Not Found',
          description: `No inventory match for ${result.barcode}`,
          variant: 'destructive',
        });
      }
    },
    [items, toast]
  );

  const handleAdjust = (item: InventoryItem, delta: number) => {
    toast({
      title: 'Quantity Updated',
      description: `${item.item_name}: ${item.quantity} → ${item.quantity + delta}`,
    });
    setSelectedItem(null);
    setAdjustQuantity(0);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-xl font-bold">Inventory</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="hidden sm:flex"
            >
              {viewMode === 'grid' ? (
                <List className="h-4 w-4" />
              ) : (
                <Grid3X3 className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="default"
              size="icon"
              onClick={() => setShowScanner(true)}
            >
              <ScanBarcode className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filters */}
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger className="w-[140px]">
              <MapPin className="h-3 w-3 mr-1" />
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc} value={loc}>
                  {loc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" size="sm" className="whitespace-nowrap">
            <Filter className="h-3 w-3 mr-1" />
            Filters
          </Button>
        </div>
      </header>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-2 px-4 py-3 bg-muted/50">
        <div className="text-center">
          <div className="text-lg font-bold text-green-600">
            {items.filter((i) => i.status === 'in_stock').length}
          </div>
          <div className="text-xs text-muted-foreground">In Stock</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-yellow-600">
            {items.filter((i) => i.status === 'low_stock').length}
          </div>
          <div className="text-xs text-muted-foreground">Low Stock</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-red-600">
            {items.filter((i) => i.status === 'out_of_stock').length}
          </div>
          <div className="text-xs text-muted-foreground">Out of Stock</div>
        </div>
      </div>

      {/* Item List */}
      <div className="p-4 space-y-3">
        {filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No items found</p>
          </div>
        ) : viewMode === 'list' ? (
          // List View
          <div className="space-y-2">
            {filteredItems.map((item) => (
              <Card
                key={item.id}
                className="cursor-pointer hover:border-primary transition-colors"
                onClick={() => {
                  setSelectedItem(item);
                  setAdjustQuantity(0);
                }}
              >
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{item.item_name}</span>
                        {getStatusBadge(item.status)}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {item.item_code} • {item.location_code}
                      </div>
                      {item.expiry_date && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-warning">
                          <Clock className="h-3 w-3" />
                          Expires: {new Date(item.expiry_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-xl font-bold">{item.quantity}</div>
                      <div className="text-xs text-muted-foreground">Min: {item.min_stock}</div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground ml-2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          // Grid View
          <div className="grid grid-cols-2 gap-3">
            {filteredItems.map((item) => (
              <Card
                key={item.id}
                className="cursor-pointer hover:border-primary transition-colors"
                onClick={() => {
                  setSelectedItem(item);
                  setAdjustQuantity(0);
                }}
              >
                <CardContent className="p-3">
                  <div className="aspect-square flex flex-col items-center justify-center">
                    <Package className="h-8 w-8 text-muted-foreground mb-2" />
                    <div className="text-center">
                      <div className="font-medium text-sm truncate w-full">
                        {item.item_name}
                      </div>
                      <div className="text-2xl font-bold mt-1">{item.quantity}</div>
                      <div className="text-xs text-muted-foreground">
                        {item.location_code}
                      </div>
                      {getStatusBadge(item.status)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Item Detail Sheet */}
      <Sheet open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedItem && (
            <>
              <SheetHeader>
                <SheetTitle>{selectedItem.item_name}</SheetTitle>
                <SheetDescription>
                  {selectedItem.item_code} • {selectedItem.category}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-6">
                {/* Stock Info */}
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div>
                    <div className="text-sm text-muted-foreground">Current Stock</div>
                    <div className="text-3xl font-bold">{selectedItem.quantity}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Min Level</div>
                    <div className="text-xl font-semibold">{selectedItem.min_stock}</div>
                  </div>
                </div>

                {/* Quick Adjust */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Quick Adjust</label>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setAdjustQuantity((q) => q - 1)}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input
                      type="number"
                      value={adjustQuantity}
                      onChange={(e) => setAdjustQuantity(parseInt(e.target.value) || 0)}
                      className="text-center text-xl font-bold"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setAdjustQuantity((q) => q + 1)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleAdjust(selectedItem, adjustQuantity)}
                      disabled={adjustQuantity === 0}
                    >
                      <Minus className="h-4 w-4 mr-2" />
                      Decrease
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={() => handleAdjust(selectedItem, adjustQuantity)}
                      disabled={adjustQuantity === 0}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Increase
                    </Button>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Location</span>
                    <span className="font-medium">{selectedItem.location}</span>
                  </div>
                  {selectedItem.batch_number && (
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Batch</span>
                      <span className="font-medium">{selectedItem.batch_number}</span>
                    </div>
                  )}
                  {selectedItem.expiry_date && (
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Expiry</span>
                      <span className="font-medium">
                        {new Date(selectedItem.expiry_date).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Status</span>
                    {getStatusBadge(selectedItem.status)}
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <Button className="w-full" variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    View History
                  </Button>
                  <Button className="w-full" variant="outline">
                    <ScanBarcode className="h-4 w-4 mr-2" />
                    Print Label
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Scanner Modal */}
      <BarcodeScanner
        open={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleScan}
        title="Scan Item Barcode"
        description="Scan item barcode or enter manually"
      />

      {/* Scan History (bottom sheet on mobile) */}
      {scanHistory.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-background border-t p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Recent Scans</span>
            <Button variant="ghost" size="sm" onClick={() => setScanHistory([])}>
              Clear
            </Button>
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {scanHistory.map((scan, idx) => (
              <Badge key={idx} variant="outline" className="whitespace-nowrap">
                {scan.type}: {scan.barcode.substring(0, 15)}...
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
